<template>
  <!-- scrollable 속성으로 내부 콘텐츠가 넘치면 v-card-text 내부에만 스크롤이 생기도록 제한합니다 -->
  <v-dialog v-model="value" content-class="custom-dialog-bottom" persistent max-width="1200px" scrollable @contextmenu.prevent>
    <v-card tile class="menu-permission-dialog">
      <!-- 다이얼로그의 상단 제목 영역을 명확히 명시 -->
      <v-card-title>
        <v-icon left>mdi-shield-account</v-icon>
        <span>{{ $t('user.menu.settings') }}</span>
      </v-card-title>

      <!-- 콘텐츠 영역: 메뉴가 많아지면 자동으로 세로 스크롤 생성 -->
      <v-card-text v-if="primeMenuList.length > 0" class="py-0 px-1">
        <!-- 대메뉴(1Depth)를 정렬하는 가로 로우 -->
        <v-row>
          <v-col v-for="(menu, index) in primeMenuList" :key="index" cols="12" sm="6" :md="columnWidth">
            <!-- 1Depth 대메뉴 카드 상자 -->
            <div class="menu-depth-1-box">
              <div class="menu-depth-1-title mb-2 d-flex align-center">
                <v-checkbox v-model="selectedMenuIdList" :value="menu.id" hide-details @change="checkMenu(menu.id)"></v-checkbox>
                <v-icon color="primary" small class="mr-1">{{ menu.icon }}</v-icon>
                <span class="font-weight-bold" v-html="$t(menu.messageCode)" />
              </div>

              <!-- 중메뉴(2Depth) 반복 구문 -->
              <div v-for="(child, cIdx) in menu.children" :key="cIdx" class="menu-depth-2-box pl-3 mb-2">
                <div class="mb-1 d-flex align-center">
                  <v-checkbox v-model="selectedMenuIdList" :value="child.id" hide-details @change="checkMenu(child.id)"></v-checkbox>
                  <v-icon small class="mr-1">{{ child.icon }}</v-icon>
                  <span class="text-subtitle-2 font-weight-medium" v-html="$t(child.messageCode)" />
                </div>

                <!-- 소메뉴(3Depth) 목록 구문 -->
                <ul class="menu-depth-3-list pl-4">
                  <li v-for="(grand, gIdx) in child.children" :key="gIdx" class="py-0">
                    <div class="d-flex align-center menu-item-link">
                      <v-checkbox v-model="selectedMenuIdList" :value="grand.id" hide-details @change="checkMenu(grand.id)"></v-checkbox>
                      <v-icon small class="mr-2">{{ grand.icon }}</v-icon>
                      <span class="text-body-2" v-html="$t(grand.messageCode)" />
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn @click="save()" small color="primary" class="px-4">{{ $t('common.save') }}</v-btn>
        <v-btn @click="close()" small color="primary" class="px-4">{{ $t('common.cancel') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { merge, isEmpty } from 'lodash';
import { api } from '@/api/common/instances';

export default {
  name: 'MenuPermissionDialog',
  props: {
    userId: String,
    userRole: String,
    value: Boolean,
  },
  data() {
    return {
      primeMenuList: [],
      prevMenuIdList: [],
      selectedMenuIdList: [],
      wholeMenuIdList: [],
    };
  },
  computed: {
    ...mapState('common/user', ['username']),
    ...mapState('common/system', ['system', 'stackType']),
    ...mapState('common/menu', ['realtime', 'analstatic', 'extraordinaries', 'preference']),
    ...mapGetters('common/system', ['getSystemById', 'getSystemsByStack']),
    isUserAdmin() {
      return this.userRole == 'ROLE_ADMIN' ? true : false;
    },
    columnWidth() {
      return this.userRole == 'ROLE_ADMIN' ? 3 : 4;
    },
  },
  watch: {
    async value() {
      if (this.value) {
        this.getPrimeMenuList();
        await this.getPreviousMenuList();
        this.selectedMenuIdList = merge([], this.prevMenuIdList);
      }
    },
  },
  methods: {
    getPrimeMenuList() {
      if (this.userRole == 'ROLE_ADMIN') {
        this.primeMenuList = this.realtime.concat(this.analstatic, this.extraordinaries, this.preference);
      } else {
        this.primeMenuList = this.realtime.concat(this.analstatic, this.extraordinaries);
      }
      const abapSstmCnt = this.getSystemsByStack('ABAP').length;
      const javaSstmCnt = this.getSystemsByStack('JAVA').length;
      const dualSstmCnt = this.getSystemsByStack('DUAL').length;

      if (abapSstmCnt == 0 && dualSstmCnt == 0) this.removeMenu(this.primeMenuList, 'abap');
      if (javaSstmCnt == 0 && dualSstmCnt == 0) this.removeMenu(this.primeMenuList, 'java');

      this.wholeMenuIdList = this.makeMenuIdArray(this.primeMenuList, []);
    },
    /**
     * CONF_USER_MTL에서 사용자에게 접근이 허용된 메뉴의 ID 목록을 읽어온다.
     */
    async getPreviousMenuList() {
      // eslint-disable-next-line prettier/prettier
      const { data: { authData}} = await api.get(`/prefer/users/${this.userId}/menu-auth`);
      if (isEmpty(authData)) {
        this.prevMenuIdList = merge([], this.wholeMenuIdList);
      } else {
        this.prevMenuIdList = authData.split(',');
      }
    },
    /**
     * 메뉴 앞의 checkbox가 클릭되었을 때 호출됨.
     * '선택'인지 '선택해제'인지 판단한 후,
     * 다음과 같이 제어해준다.
     * - 규칙1: 자식이 선택되었으면, 모든 조상도 선택되게
     * - 규칙2: 자식이 해제되었으면, 자기말고는 선택된 자매가 없으면 조상도 해제되게
     * - 규칙3: 부모가 선택되었으면, 부모의 모든 후손이 선택되게
     * - 규칙4: 부모가 해제되었으면, 부모의 모든 후손이 해제되게
     */
    checkMenu(checkedId) {
      const isChecked = this.selectedMenuIdList.includes(checkedId);

      // 빠르게 조회하기 위해 현재 선택된 ID들을 Set으로 변환
      const currentSelected = new Set(this.selectedMenuIdList);

      if (isChecked) {
        this.wholeMenuIdList.forEach(id => {
          // 규칙 3: 부모가 선택되었으면, 부모의 모든 후손(자신으로 시작하는 하위 ID)도 선택
          if (id.startsWith(checkedId + '.')) {
            currentSelected.add(id);
          }
          // 규칙 1: 자식이 선택되었으면, 모든 조상(자신의 접두사에 해당하는 상위 ID)도 선택
          if (checkedId.startsWith(id + '.')) {
            currentSelected.add(id);
          }
        });
      } else {
        // 규칙 4: 부모가 해제되었으면, 부모의 모든 후손도 해제
        this.wholeMenuIdList.forEach(id => {
          if (id.startsWith(checkedId + '.')) {
            currentSelected.delete(id);
          }
        });
        // 규칙 2: 자식이 해제되었으면, '자기 말고 선택된 자매가 없는 조상'을 순차적으로 해제
        // 깊은 자식부터 거꾸로 올라가며 체크하기 위해 문자열 길이가 긴(깊은) 순서로 조상을 정렬하여 탐색
        const ancestors = this.wholeMenuIdList.filter(id => checkedId.startsWith(id + '.')).sort((a, b) => b.length - a.length);

        ancestors.forEach(ancestor => {
          // 현재 조상(ancestor)의 직속 또는 하위 후손 중, 여전히 선택되어 있는 다른 후손이 있는지 확인
          const hasActiveDescendants = this.wholeMenuIdList.some(id => id.startsWith(ancestor + '.') && currentSelected.has(id));

          // 나 말고는 선택된 후손(자매 포함)이 아예 없다면 조상도 함께 해제
          if (!hasActiveDescendants) {
            currentSelected.delete(ancestor);
          }
        });
      }
      // Set을 다시 배열로 변환하여 반환
      this.selectedMenuIdList = Array.from(currentSelected);
    },
    /**
     * 메뉴 정보에 대하여 children 요소에 대해 재귀적으로 호출되면서
     * 메뉴ID만 추출하여 메뉴ID를 요소로 하는 새로운 배열을 생성
     */
    makeMenuIdArray(list, rsltList) {
      list.forEach(elm => {
        rsltList.push(elm.id);
        if (elm.children && elm.children.length) this.makeMenuIdArray(elm.children, rsltList);
      });
      return rsltList;
    },
    /**
     * 메뉴 목록에서 name으로 넘어온 값에 해당하는 name 속성을 가진 메뉴를 제거
     */
    removeMenu(menuList, name) {
      menuList.forEach((menu, idx, arr) => {
        if (menu.name == name) {
          arr.splice(idx, 1);
        } else {
          if (menu.children && menu.children.length) this.removeMenu(menu.children, name);
        }
      });
    },
    /**
     * Primitive type의 요소로 이루어진 배열의 동일성을 체크하는 로직
     */
    areArraysEqual(arrA, arrB) {
      // 길이가 다르면 무조건 다른 배열
      if (arrA.length !== arrB.length) return false;
      // 각 요소의 개수를 세어줄 카운터 객체 생성
      const countMap = {};
      // arrA를 돌며 요소 개수 +1
      for (const item of arrA) {
        countMap[item] = (countMap[item] || 0) + 1;
      }
      // arrB를 돌며 요소 개수 -1
      for (const item of arrB) {
        // arrA에 없는 요소이거나, 개수가 이미 0이면 일치하지 않음
        if (!countMap[item]) return false;
        countMap[item]--;
      }
      return true;
    },
    async save() {
      if (this.areArraysEqual(this.prevMenuIdList, this.selectedMenuIdList)) {
        // 변경 사항이 없으면 저장할 필요없이 바로 종료.
        this.$snackbar(this.$t('common.no.change')); // '변경 사항이 없습니다.'
        return;
      }

      if (await this.$dialog.confirm(this.$t('common.save.q'))) {
        let status = 0;
        if (this.selectedMenuIdList.length == 0) {
          // 아무런 메뉴도 선택되어 있지 않으면 CONF_USER_MTL 내역을 삭제
          // 아무런 메뉴도 선택되어 있지 않으면 모든 메뉴에 권한이 있는 것으로 보며,
          // 모든 메뉴에 권한이 있을 때에는 CONF_USER_MTL에 내역이 없다.
          ({ status } = await api.delete(`/prefer/users/${this.userId}/menu-auth`));
        } else if (this.areArraysEqual(this.selectedMenuIdList, this.wholeMenuIdList)) {
          // 전체 메뉴가 모두 선택되어 있으면 CONF_USER_MTL 내역을 삭제
          // 모든 메뉴에 권한이 있을 때에는 CONF_USER_MTL에 내역이 없다.
          ({ status } = await api.delete(`/prefer/users/${this.userId}/menu-auth`));
        } else {
          ({ status } = await api.put(`/prefer/users/${this.userId}/menu-auth`, { authData: this.selectedMenuIdList.join(',') }));
        }

        if (status === 200) {
          this.$dialog.info(this.$t('common.successful'));
          this.close();
        }
      }
    },
    close() {
      this.$emit('close', false);
    },
  },
};
</script>

<style scoped lang="scss">
/* v-snackbar가 v-dailog에 가려져서 이를 보이게 하기 위해
 * v-dialog의 위치를 중앙에서 하단으로 내림.
 */
::v-deep .custom-dialog-bottom {
  align-self: flex-end !important;
  margin-bottom: 40px !important;
}
.menu-permission-dialog {
  /* 다이얼로그 최대 높이를 브라우저 화면의 80%로 제한하여 스크롤 유도 */
  max-height: 80vh;
  display: flex;
  flex-direction: column;

  /* Vuetify 체크박스 내부의 불필요한 마진 및 리플 영역 압축 */
  ::v-deep .v-input--selection-controls {
    margin: 0 !important;
    padding: 0 !important;
    height: 24px; /* 줄 바꿈 간격에 영향을 주지 않도록 높이 고정 */
    .v-input__control {
      width: auto !important; /* 기본 너비 100% 차지하는 버그 방지 */
    }
    /* 리플 영역이 차지하는 바깥 마진 강제 리셋 */
    .v-input--selection-controls__ripple {
      margin: 0 !important;
      left: calc(50% - 12px) !important; /* 아이콘과 정중앙이 맞도록 미세 조정 */
      top: calc(50% - 12px) !important; /* 세로 정중앙 배치 */
      width: 24px !important; /* 기존 48px에서 크기 대폭 축소 */
      height: 24px !important;
    }
    /* 체크박스 기본 컨테이너 여백 리셋 */
    .v-input--selection-controls__input {
      margin-right: 0 !important;
    }
  }

  .menu-depth-1-box {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px;
    height: 100%; /* 그리드 내 카드 높이 균일화 */
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    .menu-depth-1-title {
      font-size: 1.1rem;
      border-bottom: 2px solid var(--v-primary-base, #f57c00);
      padding-bottom: 6px;
      color: var(--v-primary-base, #f57c00);
    }
  }

  .menu-depth-2-box {
    margin-top: 12px;
    border-left: 2px solid #e0e0e0;
  }

  .menu-depth-3-list {
    list-style: none;
    margin: 0;
    padding: 0;

    .menu-item-link {
      &:hover {
        color: var(--v-primary-base, #f57c00);
        cursor: default; /* 권한 관리 화면이므로 링크 이동 커서 제외 */
        .v-icon {
          color: var(--v-primary-base, #f57c00) !important;
        }
      }
    }
  }
}
</style>
